import type { RoundDelegationKind } from '../../../services/round-delegations';

/**
 * The round delegations this phone has taken (see RoundDelegation on the
 * server): the link's token and the secret the phone was given when it said
 * yes. Kept in the browser and nowhere else — **one phone per link**, so a
 * secret lost here is a helper lost until the person who shared makes a new
 * link. Hence an entry is only ever forgotten when the server says, for good,
 * that it is over (`forgetDelegation`); never on a failed request.
 */
export interface StoredDelegation {
  token: string;
  secret: string;
  competitionSlug: string;
  competitionId: string;
  roundId: string;
  roundName: string;
  kind: RoundDelegationKind;
  /** A first name and an initial — what the "you're helping" strip says. */
  grantorName: string;
}

const STORAGE_KEY = 'round-delegations';

export function loadDelegations(): StoredDelegation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (entry): entry is StoredDelegation =>
            !!entry && typeof entry.token === 'string' && typeof entry.secret === 'string'
        )
      : [];
  } catch {
    // Private mode, blocked storage, or something unreadable: nothing taken.
    return [];
  }
}

function save(entries: StoredDelegation[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Blocked storage: the page works for as long as it stays open.
  }
}

export function findDelegation(token: string | undefined): StoredDelegation | null {
  if (!token) return null;
  return loadDelegations().find((entry) => entry.token === token) ?? null;
}

export function storeDelegation(entry: StoredDelegation) {
  save([...loadDelegations().filter((stored) => stored.token !== entry.token), entry]);
}

export function forgetDelegation(token: string) {
  save(loadDelegations().filter((entry) => entry.token !== token));
}

/** The rounds of a competition this phone is helping on, for the round cards. */
export function delegationsFor(competitionSlug: string | undefined): StoredDelegation[] {
  if (!competitionSlug) return [];
  return loadDelegations().filter((entry) => entry.competitionSlug === competitionSlug);
}

/** What the phone presents: the link's token and its own secret. */
export const credentialOf = (entry: Pick<StoredDelegation, 'token' | 'secret'>) => `${entry.token}.${entry.secret}`;

/** Where a helper's pages live: under the link they came in by. */
export const helperBasePath = (entry: Pick<StoredDelegation, 'competitionSlug' | 'roundId' | 'token'>) =>
  `/competitions/${entry.competitionSlug}/rounds/${entry.roundId}/aiuto/${entry.token}`;

/** The page a helper works on: one athlete at a time, or one athlete's card. */
export const helperLandingPath = (entry: StoredDelegation) =>
  `${helperBasePath(entry)}/${entry.kind === 'team' ? 'athletes' : 'scheda'}`;
